import { useCallback, useEffect, useRef } from 'react';
import { setAppSessionId } from '../store/appSessionStore';
import { APP_SESSION_BROADCAST_CHANNEL_NAME } from '../constants';
import { APP_SESSION_TAB_ID } from '../broadcast/broadcastAppSessionLogout';

export type AppSessionBroadcastMessage =
  | { type: 'session-created'; sessionId: string }
  | { type: 'session-extended'; timestamp: number }
  | { type: 'session-logout'; reason: 'user' | 'timeout'; senderId: string };

export interface UseAppSessionBroadcastOptions {
  disabled?: boolean;
  onSessionCreated?: (sessionId: string) => void | Promise<void>;
  onSessionExtended?: (timestamp: number) => void | Promise<void>;
  onSessionLogout?: (
    reason: 'user' | 'timeout',
    senderId: string
  ) => void | Promise<void>;
}

export function useAppSessionBroadcast(
  options: UseAppSessionBroadcastOptions = {}
) {
  const channelRef = useRef<BroadcastChannel | null>(null);
  const callbacksRef = useRef(options);

  useEffect(() => {
    callbacksRef.current = options;
  }, [options]);

  useEffect(() => {
    if (options.disabled) {
      return undefined;
    }

    if (typeof BroadcastChannel !== 'undefined') {
      channelRef.current = new BroadcastChannel(
        APP_SESSION_BROADCAST_CHANNEL_NAME
      );

      channelRef.current.onmessage = (
        event: MessageEvent<AppSessionBroadcastMessage>
      ) => {
        const { data } = event;
        const callbacks = callbacksRef.current;

        switch (data.type) {
          case 'session-created':
            setAppSessionId(data.sessionId);
            callbacks
              .onSessionCreated?.(data.sessionId)
              ?.catch((err: unknown) => {
                console.error('Failed to handle session-created:', err);
              });
            break;

          case 'session-extended':
            callbacks
              .onSessionExtended?.(data.timestamp)
              ?.catch((err: unknown) => {
                console.error('Failed to handle session-extended:', err);
              });
            break;

          case 'session-logout':
            callbacks
              .onSessionLogout?.(data.reason, data.senderId)
              ?.catch((err: unknown) => {
                console.error('Failed to handle session-logout:', err);
              });
            break;

          default:
            break;
        }
      };
    }

    return () => {
      channelRef.current?.close();
      channelRef.current = null;
    };
  }, [options.disabled]);

  const broadcast = useCallback((message: AppSessionBroadcastMessage) => {
    channelRef.current?.postMessage(message);
  }, []);

  const broadcastSessionCreated = useCallback(
    (sessionId: string) => {
      broadcast({ type: 'session-created', sessionId });
    },
    [broadcast]
  );

  const broadcastSessionExtended = useCallback(
    (timestamp: number) => {
      broadcast({ type: 'session-extended', timestamp });
    },
    [broadcast]
  );

  const broadcastSessionLogout = useCallback(
    (reason: 'user' | 'timeout') => {
      broadcast({
        type: 'session-logout',
        reason,
        senderId: APP_SESSION_TAB_ID,
      });
    },
    [broadcast]
  );

  return {
    broadcast,
    broadcastSessionCreated,
    broadcastSessionExtended,
    broadcastSessionLogout,
  };
}
